<template>
  <modal-view
    title="EBM Transaction Details"
    custom-class="modal-wide"
    @close-modal="$emit('close-modal')"
  >
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"
      ></div>
    </div>

    <div
      v-else-if="error"
      class="rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4 mb-6"
    >
      <div class="flex items-center gap-3">
        <vue-feather
          type="alert-circle"
          class="text-red-600 dark:text-red-400 flex-shrink-0"
          size="20"
        />
        <div>
          <p class="text-sm font-medium text-red-800 dark:text-red-200">
            Failed to load transaction details
          </p>
          <p class="text-xs text-red-700 dark:text-red-300 mt-1">
            {{ error?.message || "Unable to fetch transaction details" }}
          </p>
        </div>
      </div>
    </div>

    <div v-else-if="transaction" class="space-y-6">
      <!-- Transaction Overview -->
      <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Transaction Overview
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >UUID</label
            >
            <p
              class="text-sm font-mono text-gray-900 dark:text-gray-100 break-all"
            >
              {{ transaction.uuid }}
            </p>
          </div>
          <div>
            <label
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >Type</label
            >
            <span
              :class="
                getTransactionTypeBadgeClass(transaction.transaction_type)
              "
              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            >
              {{ formatTransactionType(transaction.transaction_type) }}
            </span>
          </div>
          <div>
            <label
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >Status</label
            >
            <span
              :class="getStatusBadgeClass(transaction.status)"
              class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            >
              {{ transaction.status }}
            </span>
          </div>
          <div>
            <label
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >Entity</label
            >
            <p class="text-sm text-gray-900 dark:text-gray-100">
              {{ transaction.entity_type }} #{{ transaction.entity_id }}
            </p>
          </div>
          <div v-if="getInvoiceNumber(transaction)">
            <label
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >Invoice Number</label
            >
            <div class="flex items-center gap-2 mt-1">
              <span
                class="inline-flex items-center rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 px-2.5 py-0.5 text-xs font-medium"
              >
                #{{ getInvoiceNumber(transaction) }}
              </span>
            </div>
          </div>
          <div>
            <label
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >Retry Count</label
            >
            <p class="text-sm text-gray-900 dark:text-gray-100">
              {{ transaction.retry_count || 0 }}
            </p>
          </div>
          <div>
            <label
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >Retriable</label
            >
            <div class="flex items-center gap-3 mt-1">
              <span
                :class="
                  (
                    localTransactionState.isRetriable !== null
                      ? localTransactionState.isRetriable
                      : transaction.is_retriable
                  )
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                "
                class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
              >
                {{
                  (
                    localTransactionState.isRetriable !== null
                      ? localTransactionState.isRetriable
                      : transaction.is_retriable
                  )
                    ? "Yes"
                    : "No"
                }}
              </span>
              <button
                @click="handleToggleRetriable(transaction)"
                :disabled="
                  updateRetriableMutation.isPending.value || isRetryDisabled
                "
                :class="
                  (
                    localTransactionState.isRetriable !== null
                      ? localTransactionState.isRetriable
                      : transaction.is_retriable
                  )
                    ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-300 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:text-red-300 dark:border-red-700'
                    : 'bg-green-50 hover:bg-green-100 text-green-700 border-green-300 dark:bg-green-900/20 dark:hover:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                "
                class="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <vue-feather
                  v-if="updateRetriableMutation.isPending.value"
                  type="loader"
                  size="12"
                  class="animate-spin"
                />
                <vue-feather
                  v-else
                  :type="
                    (
                      localTransactionState.isRetriable !== null
                        ? localTransactionState.isRetriable
                        : transaction.is_retriable
                    )
                      ? 'x'
                      : 'check'
                  "
                  size="12"
                />
                {{
                  (
                    localTransactionState.isRetriable !== null
                      ? localTransactionState.isRetriable
                      : transaction.is_retriable
                  )
                    ? "Disable"
                    : "Enable"
                }}
              </button>
            </div>
          </div>
          <div>
            <label
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >Created At</label
            >
            <p class="text-sm text-gray-900 dark:text-gray-100">
              {{ formatDate(transaction.created_at) }}
            </p>
          </div>
          <div>
            <label
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >Updated At</label
            >
            <p class="text-sm text-gray-900 dark:text-gray-100">
              {{ formatDate(transaction.updated_at) }}
            </p>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="transaction.last_error_message" class="mt-4">
          <label
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
            >Last Error</label
          >
          <p class="text-sm text-red-600 dark:text-red-400 mt-1">
            {{ transaction.last_error_message }}
          </p>
        </div>

        <!-- Next Retry -->
        <div v-if="transaction.next_retry_at" class="mt-4">
          <label
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
            >Next Retry</label
          >
          <p class="text-sm text-gray-900 dark:text-gray-100">
            {{ formatDate(transaction.next_retry_at) }}
          </p>
        </div>
      </div>

      <!-- Request Metadata -->
      <div
        v-if="transaction.transaction_metadata"
        class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
      >
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Request Metadata
        </h3>

        <!-- Request Reference -->
        <div v-if="transaction.transaction_metadata.request_ref" class="mb-4">
          <label
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide"
          >
            Request Reference
          </label>
          <p
            class="text-sm font-mono text-gray-900 dark:text-gray-100 break-all"
          >
            {{ transaction.transaction_metadata.request_ref }}
          </p>
        </div>

        <!-- RRA EBM Metadata -->
        <div v-if="transaction.transaction_metadata.rra_ebm_metadata">
          <JSONView
            :data="transaction.transaction_metadata.rra_ebm_metadata"
            title="RRA EBM Metadata"
            :expanded="false"
            :show-raw-json="true"
          />
        </div>
      </div>

      <!-- Request Logs Timeline -->
      <div v-if="transaction.checkpoints && transaction.checkpoints.length > 0">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Request Logs Timeline
        </h3>
        <div class="space-y-4">
          <div
            v-for="(checkpoint, index) in transaction.checkpoints"
            :key="checkpoint.id || index"
            class="relative border-l-2 border-gray-200 dark:border-gray-600 pl-4 pb-4"
            :class="{ 'border-l-blue-500': checkpoint.status === 'SUCCESS' }"
          >
            <div
              class="absolute -left-2 top-0 w-4 h-4 rounded-full border-2 border-white dark:border-gray-800"
              :class="getLogStatusClass(checkpoint.status)"
            ></div>
            <div
              class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm"
            >
              <div class="flex items-start justify-between mb-3">
                <div>
                  <div class="flex items-center gap-2 mb-1">
                    <span
                      :class="
                        getRequestTypeBadgeClass(checkpoint.checkpoint_type)
                      "
                      class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    >
                      {{ formatRequestType(checkpoint.checkpoint_type) }}
                    </span>
                    <span
                      :class="getLogStatusBadgeClass(checkpoint.status)"
                      class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    >
                      {{ checkpoint.status }}
                    </span>
                  </div>
                  <p class="text-sm font-mono text-gray-600 dark:text-gray-400">
                    {{ checkpoint.endpoint }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Sequence: {{ checkpoint.sequence_order }}
                  </p>
                </div>
                <div
                  class="text-right text-xs text-gray-500 dark:text-gray-400"
                >
                  <div
                    :title="
                      checkpoint.attempted_at
                        ? new Date(checkpoint.attempted_at).toLocaleString()
                        : ''
                    "
                  >
                    {{ formatTimeAgo(checkpoint.attempted_at) }}
                  </div>
                  <div
                    v-if="checkpoint.completed_at !== checkpoint.attempted_at"
                  >
                    <span
                      :title="
                        checkpoint.completed_at
                          ? `Browser time: ${new Date(
                              checkpoint.completed_at
                            ).toLocaleString()}`
                          : ''
                      "
                    >
                      Completed: {{ formatTimeAgo(checkpoint.completed_at) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Error Message -->
              <div
                v-if="
                  checkpoint.status === 'FAILED' && checkpoint.result_message
                "
                class="mb-3"
              >
                <div
                  class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3"
                >
                  <p class="text-sm text-red-800 dark:text-red-200">
                    <strong>Error:</strong> {{ checkpoint.result_message }}
                  </p>
                  <p
                    v-if="checkpoint.result_code"
                    class="text-xs text-red-700 dark:text-red-300 mt-1"
                  >
                    Code: {{ checkpoint.result_code }}
                  </p>
                </div>
              </div>

              <!-- Success Message -->
              <div
                v-if="
                  checkpoint.status === 'SUCCESS' && checkpoint.result_message
                "
                class="mb-3"
              >
                <div
                  class="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-md p-3"
                >
                  <p class="text-sm text-green-800 dark:text-green-200">
                    {{ checkpoint.result_message }}
                  </p>
                </div>
              </div>

              <!-- Request/Response Payloads -->
              <div class="space-y-3">
                <div>
                  <JSONView
                    :data="checkpoint.input_payload"
                    title="Input Payload"
                    :expanded="expandedPayloads[`input-${index}`]"
                    :show-raw-json="false"
                    @toggle="
                      (expanded) =>
                        (expandedPayloads[`input-${index}`] = expanded)
                    "
                  />
                </div>

                <div>
                  <JSONView
                    :data="checkpoint.result_payload"
                    title="Result Payload"
                    :expanded="expandedPayloads[`result-${index}`]"
                    :show-raw-json="false"
                    @toggle="
                      (expanded) =>
                        (expandedPayloads[`result-${index}`] = expanded)
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty Logs State -->
      <div v-else class="text-center py-8">
        <vue-feather
          type="list"
          class="mx-auto text-gray-400 dark:text-gray-500 mb-3"
          size="48"
        />
        <p class="text-sm text-gray-500 dark:text-gray-400">
          No request logs available for this transaction
        </p>
      </div>

      <!-- Actions -->
      <div
        class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700"
      >
        <button
          v-if="
            (localTransactionState.isRetriable !== null
              ? localTransactionState.isRetriable
              : transaction.is_retriable) && !isRetryDisabled
          "
          @click="handleRetry"
          :disabled="retrying"
          class="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <vue-feather
            v-if="retrying"
            type="loader"
            size="16"
            class="animate-spin"
          />
          <vue-feather v-else type="refresh-cw" size="16" />
          {{ retrying ? "Retrying..." : "Retry Transaction" }}
        </button>
        <button
          @click="$emit('close-modal')"
          class="inline-flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <vue-feather type="x" size="16" />
          Close
        </button>
      </div>
    </div>
  </modal-view>
</template>

<script>
import { format } from "date-fns";
import { reactive, computed, onMounted, onUnmounted, ref } from "vue";
import { useUpdateStoreTransactionRetriable } from "@/composables/useTransactionQuery";
import { toast } from "vue3-toastify";
import JSONView from "@/components/common/JSONView.vue";

export default {
  name: "StoreTransactionDetailsModal",
  components: {
    JSONView,
  },
  props: {
    transaction: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [Object, String],
      default: null,
    },
    retrying: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close-modal", "retry"],
  setup(props) {
    const expandedPayloads = reactive({});
    const updateRetriableMutation = useUpdateStoreTransactionRetriable();
    const localTransactionState = reactive({ isRetriable: null });
    const timeAgoUpdateInterval = ref(null);

    // Debug: log transaction and checkpoints
    console.log("Transaction:", props.transaction);
    console.log("Checkpoints:", props.transaction?.checkpoints);
    if (props.transaction?.checkpoints) {
      props.transaction.checkpoints.forEach((checkpoint, index) => {
        console.log(`Checkpoint ${index}:`, checkpoint);
        if (!checkpoint.status) {
          console.error(`Checkpoint ${index} has no status:`, checkpoint);
        }
      });
    }

    const togglePayload = (type, index) => {
      const key = `${type}-${index}`;
      expandedPayloads[key] = !expandedPayloads[key];
    };

    const handleToggleRetriable = async (transaction) => {
      if (!transaction?.uuid) return;

      // Get the current state (either local or prop)
      const currentState =
        localTransactionState.isRetriable !== null
          ? localTransactionState.isRetriable
          : transaction.is_retriable;

      try {
        await updateRetriableMutation.mutateAsync({
          uuid: transaction.uuid,
          isRetriable: !currentState, // Toggle the current state
        });

        // Update the local reactive state
        localTransactionState.isRetriable = !currentState;

        toast.success(
          `Transaction ${
            localTransactionState.isRetriable
              ? "enabled for retry"
              : "disabled from retry"
          }`
        );
      } catch (error) {
        console.error("Failed to update retriable status:", error);
        toast.error("Failed to update retriable status");
      }
    };

    // Computed property to check if retry should be disabled
    const isRetryDisabled = computed(() => {
      if (!props.transaction) return true;

      // Disable retry for COMPLETED transactions - they're done, no need to retry
      return props.transaction.status === "COMPLETED";
    });

    // Lifecycle hooks for time ago updates
    onMounted(() => {
      // Start interval to update time ago display every minute
      timeAgoUpdateInterval.value = setInterval(() => {
        // Force reactivity update by triggering a re-render
        // This will cause the formatTimeAgo calls to recalculate
      }, 60000); // Update every minute
    });

    onUnmounted(() => {
      // Clean up interval when component is destroyed
      if (timeAgoUpdateInterval.value) {
        clearInterval(timeAgoUpdateInterval.value);
        timeAgoUpdateInterval.value = null;
      }
    });

    return {
      expandedPayloads,
      togglePayload,
      handleToggleRetriable,
      updateRetriableMutation,
      localTransactionState,
      isRetryDisabled,
    };
  },
  watch: {
    transaction: {
      handler(newTransaction) {
        // Reset local state when transaction changes
        if (newTransaction) {
          this.localTransactionState.isRetriable = null;
        }
      },
      immediate: true,
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        return format(new Date(dateString), "MMM dd, yyyy HH:mm:ss");
      } catch (error) {
        return "Invalid date";
      }
    },
    formatTimeAgo(isoString) {
      if (!isoString) return "N/A";
      try {
        const now = new Date();
        const time = new Date(isoString);
        const diffMs = now - time;
        const diffMins = Math.floor(diffMs / (1000 * 60));

        if (diffMins < 1) return "just now";
        if (diffMins < 60) return `${diffMins} min ago`;

        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24)
          return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
      } catch (error) {
        return "Invalid date";
      }
    },
    formatTransactionType(type) {
      if (!type) return "Unknown";
      return type
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());
    },
    formatRequestType(type) {
      if (!type) return "Unknown";

      // Map backend request types to user-friendly names
      const requestTypeMap = {
        STOCK_IO_SAVE: "Stock IO Save",
        STOCK_MASTER_SAVE: "Stock Master Save",
        SALE_SAVE: "Sale Save",
        PRODUCT_SAVE: "Product Save",
        SERVICE_SAVE: "Service Save",
        PURCHASE_SAVE: "Purchase Save",
        IMPORT_SAVE: "Import Save",
        BRANCH_CUSTOMER_SAVE: "Customer Save",
        UNKNOWN: "Unknown",
      };

      return (
        requestTypeMap[type] ||
        type
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (l) => l.toUpperCase())
      );
    },
    getTransactionTypeBadgeClass() {
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    },
    getStatusBadgeClass(status) {
      const statusClasses = {
        FAILED: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
        PARTIALLY_COMPLETED:
          "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200",
        PENDING:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
        IN_PROGRESS:
          "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200",
        COMPLETED:
          "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
      };
      return (
        statusClasses[status] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200"
      );
    },
    getRequestTypeBadgeClass() {
      return "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200";
    },
    getLogStatusBadgeClass(status) {
      const statusClasses = {
        SUCCESS:
          "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200",
        FAILED: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200",
        PENDING:
          "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200",
        SKIPPED:
          "bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200",
      };
      return (
        statusClasses[status] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200"
      );
    },
    getLogStatusClass(status) {
      const statusClasses = {
        SUCCESS: "bg-green-500",
        FAILED: "bg-red-500",
        PENDING: "bg-yellow-500",
        SKIPPED: "bg-gray-500",
      };
      return statusClasses[status] || "bg-gray-500";
    },
    handleRetry() {
      this.$emit("retry", this.transaction.uuid);
    },
    getInvoiceNumber(transaction) {
      if (!transaction?.checkpoints) return null;

      // Find the first SALE_SAVE checkpoint
      const saleSaveLog = transaction.checkpoints.find(
        (checkpoint) => checkpoint.checkpoint_type === "SALE_SAVE"
      );

      if (!saleSaveLog?.input_payload?.invcNo) return null;

      return saleSaveLog.input_payload.invcNo;
    },
  },
};
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
